import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SectionHeading } from "@/components/site/SectionHeading";
import { MarqueeTrack, useMarquee } from "@/components/site/ScrollTrack";
import { useLiveProperties } from "@/hooks/useLiveProperties";
import { api, type CommunitySummary } from "@/lib/api";
import { formatAEDCompact } from "@/lib/format";
import { SafeImage } from "@/components/site/SafeImage";
import { COMMUNITIES, findCommunityContent } from "@/lib/communities";

const LISTING_TYPE_LABELS: Record<"sale" | "rent" | "off-plan", string> = {
  sale: "Sale",
  rent: "Rent",
  "off-plan": "Off-Plan",
};

type CommunityCardData = {
  name: string;
  slug: string;
  count: number;
  minPrice: number;
  coverImage?: string;
  highlight?: string;
  listingTypes: ("sale" | "rent" | "off-plan")[];
  latestListedAt: string;
};

/**
 * Homepage communities section. Editorial community cards keep the
 * homepage stable, while live counts/type mix are merged in from the
 * property feed when available. The cards ride a full-bleed carousel
 * that drifts rightwards on its own — the opposite way to the developer
 * carousel directly above it — and pauses under the pointer or finger.
 */
export function AreasGrid() {
  const { version } = useLiveProperties();
  const [communities, setCommunities] = useState<CommunitySummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .listCommunities()
      .then(({ communities }) => !cancelled && setCommunities(communities))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [version]);

  const top: CommunityCardData[] = COMMUNITIES.map((community) => {
    const live =
      communities.find((item) => item.slug === community.slug) ??
      communities.find(
        (item) => item.name.toLowerCase() === community.name.toLowerCase(),
      );

    return {
      name: community.name,
      slug: community.slug,
      count: live?.count ?? 0,
      minPrice: live?.minPrice ?? 0,
      coverImage: community.fallbackCover,
      highlight: community.highlight,
      listingTypes:
        live?.listingTypes ?? ([] as ("sale" | "rent" | "off-plan")[]),
      latestListedAt: live?.latestListedAt ?? "",
    };
  });

  const { trackRef } = useMarquee({ speed: 36, direction: -1 }, [loading, top.length]);

  return (
    <section className="relative overflow-hidden border-t border-[var(--line)] bg-[var(--bg-2)] pb-12 sm:pb-16 md:pb-24 short:pb-10">
      <div className="relative container-wide pt-12 sm:pt-16 md:pt-24 short:pt-10">
        <SectionHeading
          eyebrow="Where We Operate"
          title="Dubai's Best Neighbourhoods."
          body="From iconic waterfronts to serene villa communities, every neighbourhood we operate in, expertly covered."
          aside={
            <Link to="/areas" className="link-arrow tap-target">
              Explore All Communities
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M2 7h10m-4-4 4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.2"
                />
              </svg>
            </Link>
          }
        />
      </div>

      {loading && (
        <ul className="marquee-track mt-8 gap-4 sm:mt-10 sm:gap-5" aria-busy>
          {Array.from({ length: 6 }).map((_, index) => (
            <li
              key={index}
              className="min-h-[278px] w-[78%] max-w-[300px] animate-pulse bg-[var(--surface)] sm:w-[300px] sm:max-w-none"
            >
              <span className="sr-only">Loading community</span>
            </li>
          ))}
        </ul>
      )}

      {!loading && top.length > 0 && (
        <MarqueeTrack
          trackRef={trackRef}
          className="mt-8 gap-4 sm:mt-10 sm:gap-5"
          items={top}
          keyOf={(community) => community.slug}
          itemClassName="w-[78%] max-w-[300px] sm:w-[300px] sm:max-w-none"
          renderItem={(community, repeat) => (
            <CommunityCard community={community} tabIndex={repeat ? -1 : undefined} />
          )}
        />
      )}

      {!loading && top.length === 0 && (
        <p className="container-wide py-10 text-center text-[var(--text-soft)]">
          No communities available yet.
        </p>
      )}
    </section>
  );
}

function CommunityCard({
  community,
  tabIndex,
}: {
  community: CommunityCardData;
  tabIndex?: number;
}) {
  const content =
    findCommunityContent(community.slug) ?? findCommunityContent(community.name);
  const target = content
    ? `/areas/${content.slug}`
    : `/properties?community=${encodeURIComponent(community.name)}`;
  const cover = community.coverImage ?? content?.fallbackCover;

  return (
    <Link
      to={target}
      tabIndex={tabIndex}
      data-on-dark
      className="group relative isolate flex min-h-[278px] overflow-hidden border border-[rgba(211,197,150,0.18)] bg-[var(--navy)] shadow-[0_20px_60px_-46px_rgba(13,20,34,0.45)]"
    >
      {cover ? (
        <SafeImage
          src={cover}
          alt={`${community.name}, Dubai`}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover opacity-[0.84] transition-transform duration-[900ms] group-hover:scale-[1.04]"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-[var(--navy)]">
          <span className="font-display text-6xl text-[var(--gold-bright)]/40">
            {community.name.charAt(0)}
          </span>
        </div>
      )}
      <span
        aria-hidden
        className="absolute inset-0 bg-[linear-gradient(180deg,rgba(13,20,34,0.12)_0%,rgba(13,20,34,0.22)_34%,rgba(13,20,34,0.86)_100%)]"
      />

      <div className="relative flex min-h-full w-full flex-col justify-end p-4 sm:p-5">
        <div>
          {community.listingTypes.length > 0 && (
            <p className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.16em] text-[var(--cream)]/85">
              {community.listingTypes.map((t) => LISTING_TYPE_LABELS[t]).join(" · ")}
            </p>
          )}
          <h3
            className="break-words font-display text-[24px] leading-[1.05] text-[var(--text-on-dark)] transition-colors duration-200 group-hover:text-[var(--gold-bright)] sm:text-[28px]"
            style={{ fontVariationSettings: '"opsz" 72' }}
          >
            {community.name}
          </h3>
          <div className="mt-4 flex items-end justify-between gap-3 border-t border-[rgba(211,197,150,0.24)] pt-3">
            <span className="min-w-0">
              <span className="block text-[13px] text-[var(--cream)] tabular-nums">
                {community.minPrice > 0
                  ? `From ${formatAEDCompact(community.minPrice)}`
                  : "Ask Hive Homes"}
              </span>
            </span>
            <svg
              className="size-5 flex-none text-[var(--cream)] transition-transform duration-200 group-hover:translate-x-1 group-hover:text-[var(--gold-bright)]"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden
            >
              <path d="M4 10h12m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.2" />
            </svg>
          </div>
        </div>
      </div>
    </Link>
  );
}


